import React, { useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Header } from '@/components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { 
  Download, 
  Chrome, 
  CheckCircle2, 
  Copy,
  MessageSquare,
  Send,
  Eye,
  Sparkles,
  Key,
  Edit3,
  ArrowRight,
  AlertCircle
} from 'lucide-react';

export const ExtensionPage = () => {
  const { token } = useAuth();
  const [copied, setCopied] = useState(false);
  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  const handleCopyToken = async () => {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      toast.success('Token copié !');
      setTimeout(() => setCopied(false), 3000);
    } catch {
      toast.info('Impossible de copier automatiquement');
    }
  };

  const handleCopyApiUrl = async () => {
    try {
      await navigator.clipboard.writeText(apiUrl);
      toast.success('URL API copiée !');
    } catch {
      toast.info('Impossible de copier automatiquement');
    }
  };

  return (
    <MainLayout>
      <Header 
        title="Extension Chrome" 
        subtitle="Rédigez des avis et répondez aux commentaires en un clic" 
      />

      <div className="p-6 space-y-6 animate-fadeIn max-w-4xl" data-testid="extension-page">
        {/* Hero */}
        <Card className="bg-gradient-to-br from-[#FF5A6E] to-[#FF3B55] text-white border-0">
          <CardContent className="p-8">
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <Chrome className="h-8 w-8" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-2">Goodtime Review Copilot v2.8</h2>
                <p className="text-white/90 mb-4">
                  L'extension fonctionne en 2 modes : <strong>rédiger un avis voyageur</strong> (page d'écriture Airbnb) 
                  et <strong>répondre aux commentaires</strong> (page des avis Airbnb/Booking.com).
                </p>
                <a
                  href={`${apiUrl}/api/extension/download`}
                  download="goodtime-review-copilot-extension.zip"
                  className="inline-flex items-center justify-center rounded-md text-sm font-medium h-10 px-4 py-2 bg-white text-[#FF5A6E] hover:bg-white/90"
                  data-testid="download-extension-btn"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Télécharger l'extension v2.8
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Important: re-download notice */}
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-900">Mise à jour importante (v2.8)</p>
              <p className="text-sm text-amber-700 mt-1">
                Si vous avez déjà installé l'extension, <strong>re-téléchargez et rechargez-la</strong> dans Chrome 
                pour activer le support de <code className="bg-amber-100 px-1 rounded">fr.airbnb.com</code> et le mode "rédiger un avis".
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Configuration */}
        <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Key size={18} className="text-[#FF5A6E]" />
              Configuration de l'extension
            </CardTitle>
            <CardDescription>Configurez ces informations dans le popup de l'extension</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">URL de l'API</label>
              <div className="flex gap-2">
                <Input value={apiUrl} readOnly className="font-mono text-sm bg-slate-50" data-testid="api-url-input" />
                <Button variant="outline" size="icon" onClick={handleCopyApiUrl} data-testid="copy-api-url-btn">
                  <Copy size={16} />
                </Button>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Token d'authentification</label>
              <div className="flex gap-2">
                <Input value={token ? `${token.substring(0, 30)}...` : 'Non connecté'} readOnly className="font-mono text-sm bg-slate-50" data-testid="auth-token-input" />
                <Button variant="outline" size="icon" onClick={handleCopyToken} disabled={!token} data-testid="copy-token-btn">
                  {copied ? <CheckCircle2 size={16} className="text-emerald-500" /> : <Copy size={16} />}
                </Button>
              </div>
              <p className="text-xs text-slate-400 mt-1">Ce token expire après 7 jours. Revenez ici pour en copier un nouveau.</p>
            </div>
          </CardContent>
        </Card>

        {/* Two modes explained */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <CardHeader className="pb-2">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center mb-2">
                <Edit3 className="h-5 w-5 text-blue-600" />
              </div>
              <CardTitle className="text-base">Mode 1 : Rédiger un avis voyageur</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-slate-600 space-y-3">
              <p>Quand vous êtes sur la page <strong>d'écriture d'avis</strong> Airbnb (URL avec <code className="bg-slate-100 px-1 rounded">/edit</code>)</p>
              <ol className="list-decimal list-inside space-y-1.5 text-sm">
                <li>Le bouton <strong>"Générer l'avis"</strong> apparaît en bas à droite</li>
                <li>Cliquez dessus, l'IA détecte le nom du voyageur</li>
                <li>Elle génère un avis basé sur vos données Beds24</li>
                <li>Cliquez <strong>"Injecter"</strong> pour remplir le formulaire Airbnb</li>
                <li>Relisez, ajustez si nécessaire, et validez !</li>
              </ol>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <CardHeader className="pb-2">
              <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center mb-2">
                <MessageSquare className="h-5 w-5 text-violet-600" />
              </div>
              <CardTitle className="text-base">Mode 2 : Répondre à un commentaire</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-slate-600 space-y-3">
              <p>Quand vous êtes sur la page <strong>des avis reçus</strong> (Airbnb ou Booking.com)</p>
              <ol className="list-decimal list-inside space-y-1.5 text-sm">
                <li>Le bouton <strong>"Goodtime"</strong> apparaît en bas à droite</li>
                <li>L'extension lit le commentaire du voyageur</li>
                <li>L'IA génère une réponse adaptée au ton</li>
                <li>Cliquez <strong>"Injecter"</strong> ou <strong>"Copier"</strong></li>
                <li>Vous pouvez aussi <strong>importer</strong> les commentaires pour analyse</li>
              </ol>
            </CardContent>
          </Card>
        </div>

        {/* Installation */}
        <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
          <CardHeader>
            <CardTitle className="text-lg">Installation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {[
              { step: 1, title: 'Téléchargez et dézippez', desc: 'Cliquez sur "Télécharger" et dézippez le fichier' },
              { step: 2, title: 'Installez dans Chrome', desc: <>Allez sur <code className="bg-slate-200 px-1.5 py-0.5 rounded text-xs">chrome://extensions</code>, activez le "Mode développeur", puis "Charger l'extension non empaquetée"</> },
              { step: 3, title: 'Configurez', desc: 'Ouvrez le popup de l\'extension et collez l\'URL API et le token ci-dessus' },
              { step: 4, title: 'Utilisez !', desc: 'Allez sur une page d\'avis Airbnb ou Booking — le bouton Goodtime apparaît automatiquement' },
            ].map(item => (
              <div key={item.step} className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-[#FF5A6E] text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                  {item.step}
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">{item.title}</h4>
                  <p className="text-sm text-slate-600 mt-1">{item.desc}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Platform capabilities */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <CardContent className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-[#FF5A5F] flex items-center justify-center">
                  <span className="text-white font-bold text-xl">A</span>
                </div>
                <h3 className="font-semibold text-slate-900">Airbnb</h3>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-emerald-600">
                  <CheckCircle2 size={14} />
                  <span>Rédiger un avis voyageur (via extension)</span>
                </div>
                <div className="flex items-center gap-2 text-emerald-600">
                  <CheckCircle2 size={14} />
                  <span>Répondre aux commentaires (via extension)</span>
                </div>
                <div className="flex items-center gap-2 text-emerald-600">
                  <CheckCircle2 size={14} />
                  <span>Importer les commentaires pour analyse</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <CardContent className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-[#0071C2] flex items-center justify-center">
                  <span className="text-white font-bold text-xl">B</span>
                </div>
                <h3 className="font-semibold text-slate-900">Booking.com</h3>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-slate-400">
                  <span className="w-3.5 h-3.5 rounded-full border border-slate-300 flex items-center justify-center text-[8px]">—</span>
                  <span>Pas d'avis voyageurs (non supporté)</span>
                </div>
                <div className="flex items-center gap-2 text-emerald-600">
                  <CheckCircle2 size={14} />
                  <span>Répondre aux commentaires (via extension)</span>
                </div>
                <div className="flex items-center gap-2 text-emerald-600">
                  <CheckCircle2 size={14} />
                  <span>Importer les commentaires pour analyse</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default ExtensionPage;
